import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material'
import { ForwardToInbox as ForwardEmailIcon } from '@mui/icons-material'
import { ticketForwardingService } from '@shared/services/TicketForwardingService'
import { ForwardTicketRequest } from '@shared/types/entities'

interface ForwardTicketDialogProps {
  open: boolean
  onClose: () => void
  ticketId: string
  ticketNumber: string
  attachmentCount: number
  onForwardSuccess?: () => void
}

export function ForwardTicketDialog({
  open,
  onClose,
  ticketId,
  ticketNumber,
  attachmentCount,
  onForwardSuccess
}: ForwardTicketDialogProps) {
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [forwardingNote, setForwardingNote] = useState('')
  const [includeAttachments, setIncludeAttachments] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Validation states
  const [emailError, setEmailError] = useState<string | null>(null)
  const [noteError, setNoteError] = useState<string | null>(null)

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) {
      setEmailError('Email is required')
      return false
    }
    if (!emailRegex.test(email)) {
      setEmailError('Invalid email format')
      return false
    }
    setEmailError(null)
    return true
  }

  const validateNote = (note: string): boolean => {
    if (!note || note.trim().length < 10) {
      setNoteError('Note must be at least 10 characters')
      return false
    }
    if (note.length > 2000) {
      setNoteError('Note must be less than 2000 characters')
      return false
    }
    setNoteError(null)
    return true
  }

  const handleSubmit = async () => {
    // Validate inputs
    const isEmailValid = validateEmail(recipientEmail)
    const isNoteValid = validateNote(forwardingNote)

    if (!isEmailValid || !isNoteValid) {
      return
    }

    try {
      setLoading(true)
      setError(null)

      const request: ForwardTicketRequest = {
        recipientEmail: recipientEmail.trim(),
        recipientName: recipientName.trim() || undefined,
        forwardingNote: forwardingNote.trim(),
        includeAttachments
      }

      await ticketForwardingService.forwardTicket(ticketId, request)

      setSuccess(true)

      // Call success callback if provided
      if (onForwardSuccess) {
        onForwardSuccess()
      }

      // Close dialog after short delay
      setTimeout(() => {
        handleClose()
      }, 1500)
    } catch (err: any) {
      setError(err.message || 'Failed to forward ticket')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setRecipientEmail('')
      setRecipientName('')
      setForwardingNote('')
      setIncludeAttachments(true)
      setError(null)
      setSuccess(false)
      setEmailError(null)
      setNoteError(null)
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ForwardEmailIcon />
          <Typography variant="h6">
            Forward Ticket {ticketNumber}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Ticket forwarded successfully!
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Recipient Email *"
            type="email"
            fullWidth
            value={recipientEmail}
            onChange={(e) => {
              setRecipientEmail(e.target.value)
              if (emailError) validateEmail(e.target.value)
            }}
            onBlur={() => validateEmail(recipientEmail)}
            error={!!emailError}
            helperText={emailError || 'Email address of the recipient'}
            disabled={loading || success}
            autoFocus
          />

          <TextField
            label="Recipient Name (Optional)"
            fullWidth
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            helperText="Name of the recipient (optional)"
            disabled={loading || success}
          />

          <TextField
            label="Forwarding Note *"
            multiline
            rows={4}
            fullWidth
            value={forwardingNote}
            onChange={(e) => {
              setForwardingNote(e.target.value)
              if (noteError) validateNote(e.target.value)
            }}
            onBlur={() => validateNote(forwardingNote)}
            error={!!noteError}
            helperText={noteError || `${forwardingNote.length}/2000 characters. Explain why you're forwarding this ticket.`}
            disabled={loading || success}
            inputProps={{ maxLength: 2000 }}
          />

          {attachmentCount > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeAttachments}
                  onChange={(e) => setIncludeAttachments(e.target.checked)}
                  disabled={loading || success}
                />
              }
              label={`Include ${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}`}
            />
          )}

          <Alert severity="info">
            The recipient will receive an email with the ticket details
            {includeAttachments && attachmentCount > 0 ? ' and attachments' : ''}.
            You will remain assigned to this ticket.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} /> : <ForwardEmailIcon />}
        >
          {loading ? 'Forwarding...' : 'Forward Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
